import classNames from 'classnames';
import {Button} from 'components/Button';
import {Typography} from 'components/Typography';
import Link from 'components/CustomRoutingLink';
import React, {FC, MouseEventHandler, ReactNode, useRef} from 'react';
import style from './style.module.scss';
import Icon from 'components/Icon';
import {useRouter} from 'next/router';
import {useClickOutside} from 'core/hooks/useClickOutside';

export const TOAST_THEMES = {
    ERROR: 'error',
    INFO: 'info',
    WARNING: 'warning',
};

export type TTheme = 'info' | 'error' | 'warning' | 'notification';

export interface IProps {
    className?: string;
    text: string;
    path?: string;
    closeToast?: () => void;
    buttonText?: string;
    isNeedCloseButton?: boolean;
    hasIcon?: boolean;
    closeOnClick?: boolean;
    closeOnClickOutside?: boolean;
    theme?: TTheme;
    icon?: ReactNode;
}

export const Toast: FC<IProps> = ({
    className,
    path,
    text,
    closeToast,
    closeOnClick,
    buttonText,
    closeOnClickOutside = false,
    isNeedCloseButton = false,
    hasIcon = true,
    theme = TOAST_THEMES.INFO,
}) => {
    const router = useRouter();
    const ref = useRef<HTMLDivElement>(null);

    const handleClick: MouseEventHandler<HTMLButtonElement> = (e) => {
        e.stopPropagation();

        closeToast?.();
    };

    useClickOutside([ref], () => {
        if (closeOnClickOutside) {
            closeToast?.();
        }
    });

    return (
        <div
            ref={ref}
            className={classNames(style.toast, className, {
                [style[theme]]: theme,
                [style.hasCloseButton]: isNeedCloseButton,
            })}
        >
            <div
                className={classNames(style.toastContent, {[style.toastContent_withLink]: Boolean(path)})}
                onClick={() => {
                    if (path) {
                        router.push(path);
                    }

                    if (closeOnClick) {
                        closeToast?.();
                    }
                }}
            >
                {hasIcon && <Icon className={style.icon} icon={'check-button'} />}
                <Typography element={'p'} variant={'notification' === theme ? 'label-14-r' : 'p'}>
                    {text}
                </Typography>
                {isNeedCloseButton && (
                    <button className={style.closeButton} onClick={handleClick}>
                        <Icon icon={'close'} />
                    </button>
                )}
            </div>
            {path && buttonText && (
                <Link href={path} legacyBehavior={true}>
                    <a>
                        <Button isWithoutPaddings={true} onClick={handleClick} theme={'ghost'}>
                            {buttonText}
                        </Button>
                    </a>
                </Link>
            )}
        </div>
    );
};
